import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import GetIcon from '../../utils/Icon';
import DateRangeInput from '../../Core/Fields/DateRangeInput';
import { IoClose, IoChevronDown, IoChevronUp } from 'react-icons/io5';

const SearchFilters = ({
  filtersConfig,
  onFilterChange,
  onSettingsToggle,
  className = '',
  inputClassName = 'form-control inputdesign',
  inputStyle = { fontSize: '0.8rem' }
}) => {
  const [filterValues, setFilterValues] = useState(
    filtersConfig.reduce((acc, filter) => {
      acc[filter.name] = filter.defaultValue || (filter.multiSelect ? [] : '');
      return acc;
    }, {})
  );

  const [dropdownOpen, setDropdownOpen] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const searchInputRefs = useRef({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValues = { ...filterValues, [name]: value };
    setFilterValues(newValues);
    if (onFilterChange) onFilterChange(newValues);
  };

  const toggleDropdown = (name) => {
    setDropdownOpen((prev) => {
      const updated = { ...prev, [name]: !prev[name] };
      if (!updated[name]) setSearchTerms((s) => ({ ...s, [name]: '' }));
      return updated;
    });
  };

  const toggleOptionSelection = (name, value) => {
    const current = filterValues[name] || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    const newValues = { ...filterValues, [name]: updated };
    setFilterValues(newValues);
    if (onFilterChange) onFilterChange(newValues);
  };

  const isOptionSelected = (name, value) =>
    (filterValues[name] || []).includes(value);

  const handleSearchChange = (name, term) => {
    setSearchTerms((prev) => ({ ...prev, [name]: term }));
  };

  const clearSelection = (name, e) => {
    e.stopPropagation();
    const newValues = { ...filterValues, [name]: [] };
    setFilterValues(newValues);
    if (onFilterChange) onFilterChange(newValues);
  };

  const getSelectedLabels = (filter) => {
    const selected = filterValues[filter.name] || [];
    const labels = filter.options
      .filter((opt) => selected.includes(opt.value))
      .map((opt) => opt.label);
    return labels.join(', ');
  };

  const filteredOptions = (filter) => {
    const term = (searchTerms[filter.name] || '').toLowerCase();
    return filter.options?.filter((opt) =>
      opt.label.toLowerCase().includes(term)
    ) || [];
  };

  return (
    <div className={`d-flex justify-content-between align-items-center p-2 pt-0 ${className}`}>
      {filtersConfig.map((filter) => (
        <div key={filter.name} style={{ width: filter.width || 'auto', flexShrink: 0, position: 'relative' }}>
          
          {/* Normal Select */}
          {filter.type === 'select' && (
            <div className="form-floating" style={{ fontSize: '0.875rem' }}>
              <select
                className={`form-select form-select-sm ${inputClassName}`}
                id={`floatingSelect-${filter.name}`}
                name={filter.name}
                value={filterValues[filter.name]}
                onChange={handleChange}
                style={{
                  height: 'calc(2rem + 2px)',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  color: filterValues[filter.name] ? '#000' : '#6c757d',
                  ...inputStyle,
                }}
              >
                <option value="">
                  {filter.placeholder || `Select ${filter.label}`}
                </option>
                {filter.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {filterValues[filter.name] && (
                <label htmlFor={`floatingSelect-${filter.name}`}>
                  {filter.label}
                </label>
              )}
            </div>
          )}

          {/* SelectSearch Multi-Select with Search */}
          {filter.type === 'selectsearch' && (
            <div style={{ position: 'relative' }}>
              <div
                className={`${inputClassName}`}
                style={{
                  cursor: 'pointer',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #ced4da',
                  height: '2.4rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#fff',
                    height: 'calc(2rem + 2px)',
                  ...inputStyle
                }}
                onClick={() => toggleDropdown(filter.name)}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    flexGrow: 1,
                    color: getSelectedLabels(filter) ? '#000' : '#6c757d'
                  }}
                >
                  {getSelectedLabels(filter) || filter.placeholder || filter.label}
                </span>
                <span className="ps-2">
                  {dropdownOpen[filter.name] ? <IoChevronUp size={14} /> : <IoChevronDown size={14} />}
                </span>
              </div>

              {/* Floating Label */}
              <label
                style={{
                  position: 'absolute',
                  left: '0.8rem',
                  top: dropdownOpen[filter.name] || filterValues[filter.name]?.length > 0 ? '-0.7rem' : '0.6rem',
                  fontSize: dropdownOpen[filter.name] || filterValues[filter.name]?.length > 0 ? '0.75rem' : '0',
                  color: '#6c757d',
                  background: '#fff',
                  padding: '0 4px',
                  transition: 'all 0.2s ease',
                  pointerEvents: 'none',
                }}
              >
                {filter.label}
              </label>

              {dropdownOpen[filter.name] && (
                <div
                  className="custom-scrollbar"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    marginTop: '2px'
                  }}
                >
                  <div style={{ padding: '6px', borderBottom: '1px solid #eee' }}>
                    <input
                      ref={el => (searchInputRefs.current[filter.name] = el)}
                      type="text"
                      placeholder={`Search ${filter.label}...`}
                      value={searchTerms[filter.name] || ''}
                      onChange={(e) => handleSearchChange(filter.name, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        borderRadius: '3px',
                        fontSize: '0.8rem',
                        outline: 'none',
                        height: '28px'
                      }}
                    />
                  </div>
                  <div>
                    {filteredOptions(filter).length > 0 ? (
                      filteredOptions(filter).map(option => (
                        <div
                          key={option.value}
                          style={{
                            fontSize: '0.8rem',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            backgroundColor: isOptionSelected(filter.name, option.value) ? '#f8f9fa' : 'white',
                            borderLeft: isOptionSelected(filter.name, option.value)
                              ? '3px solid #e69500'
                              : '3px solid transparent'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOptionSelection(filter.name, option.value);
                          }}
                        >
                          {filter.multiSelect && (
                            <input
                              type="checkbox"
                              checked={isOptionSelected(filter.name, option.value)}
                              readOnly
                              style={{
                                marginRight: '8px',
                                accentColor: '#391f84'
                              }}
                            />
                          )}
                          {option.label}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '10px', color: '#6c757d', textAlign: 'center' }}>
                        No options found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Input Types */}
          {filter.type !== 'select' && filter.type !== 'selectsearch' && filter.type !== 'date-range' && (
            <div className="form-floating" style={{ fontSize: '0.875rem' }}>
              <input
                type={filter.type || 'text'}
                name={filter.name}
                className={`form-control form-control-sm ${inputClassName}`}
                style={{
                  height: 'calc(2rem + 2px)',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.4rem',
                  ...inputStyle
                }}
                placeholder={filter.placeholder || filter.label}
                value={filterValues[filter.name]}
                onChange={handleChange}
                id={`floatingInput-${filter.name}`}
              />
              <label
                htmlFor={`floatingInput-${filter.name}`}
                style={{
                  padding: '0.2rem 0.5rem',
                  transform: 'scale(0.85) translateY(0.1rem) translateX(0.15rem)'
                }}
              >
                {filter.placeholder || filter.label}
              </label>
            </div>
          )}

          {/* Date Range Input */}
          {filter.type === 'date-range' && (
            <DateRangeInput
              name={filter.name}
              onChange={handleChange}
              inputClassName={inputClassName}
              placeholder={filter.placeholder || filter.label}
              label={filter.label}
            />
          )}
        </div>
      ))}

      {onSettingsToggle && (
        <div style={{ width: '5.7%', flexShrink: 0 }}>
          <button
            onClick={onSettingsToggle}
            className="importbtn"
            style={{
              backgroundColor: '#fff',
              textDecoration: 'none',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Settings
            <span className="iconbox ps-2">
              <GetIcon iconName="IoSettingsOutline" size={14} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

SearchFilters.propTypes = {
  filtersConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string,
      type: PropTypes.oneOf(['text', 'date', 'number', 'select', 'date-range', 'selectsearch']),
      placeholder: PropTypes.string,
      width: PropTypes.string,
      defaultValue: PropTypes.any,
      multiSelect: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  onFilterChange: PropTypes.func,
  onSettingsToggle: PropTypes.func,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  inputStyle: PropTypes.object,
};

SearchFilters.defaultProps = {
  filtersConfig: [],
  inputClassName: 'form-control inputdesign',
  inputStyle: { fontSize: '0.8rem' },
};

export default SearchFilters;
